import { useState } from 'react';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { useQuery } from '@tanstack/react-query';
import {
  DownloadSimpleIcon,
  FunctionIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { T, useGT, useLocale } from 'gt-react';
import {
  type WorkbookSource,
  type WorkbookTable as TableData,
} from '@erp/shared-types';
import { orpc, rpcClient } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { startDownloadInFrame } from '@/api/rpc/downloadUrl';
import { WorkbookFilePreviewButton } from './WorkbookFilePreview';
import { WorkbookSourceOrigin } from './WorkbookSourceOrigin';
import {
  catalogGaps,
  sourceColumnTypes,
  type WorkbookCatalogKind,
} from './workbookCatalog';
import { WorkbookSourcePreview } from './WorkbookSourcePreview';
import { useSourcePreview } from './useSourcePreview';
import { sourceFilterTarget } from './WorkbookCellMenu';
import type { WorkbookEditor } from './useWorkbookEditor';

export function SourcePanel({
  editor,
  source,
  addSheet,
  openItem,
  busy = false,
}: {
  editor: WorkbookEditor;
  source: WorkbookSource;
  openItem: (id: string) => void;
  addSheet: (source: WorkbookSource, table: TableData) => void;
  busy?: boolean;
}) {
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const table = useSourcePreview(editor.workbookId, source);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-medium">{source.name}</h2>
        <Button
          size="sm"
          disabled={!table.data || !canUpdate}
          onClick={() => {
            if (table.data) addSheet(source, table.data);
          }}
        >
          <PlusIcon />
          <T context="workbook create worksheet">Create worksheet</T>
        </Button>
      </div>
      <WorkbookSourceOrigin
        source={source}
        editor={editor}
        openItem={openItem}
      />
      <p className="text-xs text-muted-foreground">
        <T context="workbook source preview note">
          Source preview, up to 100 rows. Runs capture the complete input within
          the workbook limits.
        </T>
      </p>
      <WorkbookSourcePreview
        key={source.id}
        data={table.data}
        error={table.error}
        pending={table.isFetching}
        types={sourceColumnTypes(source, editor.catalog.data)}
        filterTarget={sourceFilterTarget(
          editor.definition.sheets,
          source.id,
          canUpdate && !busy
            ? (id) => editor.edits.columnarSheet(id).update
            : undefined,
        )}
      />
    </div>
  );
}
export function FilePanel({
  editor,
  fileId,
  onSource,
}: {
  editor: WorkbookEditor;
  fileId: string;
  onSource: (source: WorkbookSource) => void;
}) {
  const gt = useGT();
  const [canUpdate] = useCan(Action.Update, Subject.Workbook);
  const file = editor.definition.files.find((item) => item.id === fileId);
  const [sheetName, setSheetName] = useState(file?.sheets[0] ?? '');
  const [headerRow, setHeaderRow] = useState('1');
  const validHeader =
    /^\d+$/.test(headerRow) &&
    Number(headerRow) >= 1 &&
    Number(headerRow) <= 10000;
  const [startRow, setStartRow] = useState(1);
  const inspection = useQuery(
    orpc.workbooks.inspect.queryOptions({
      input: { workbookId: editor.workbookId, fileId, sheetName, startRow },
    }),
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="min-w-0 flex-1 font-medium">{file?.name}</h2>
        <WorkbookFilePreviewButton
          title={file?.fileName ?? file?.name ?? ''}
          load={() =>
            rpcClient.workbooks.fileDownload({
              workbookId: editor.workbookId,
              fileId,
            })
          }
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          onClick={() =>
            void rpcClient.workbooks
              .fileDownload({ workbookId: editor.workbookId, fileId })
              .then(({ url }) => startDownloadInFrame(url))
              .catch(() =>
                toast.error(
                  gt("This file couldn't be downloaded. Try again.", {
                    context: 'workbook attached file download error',
                  }),
                ),
              )
          }
        >
          <DownloadSimpleIcon />
          <T context="workbook attached file download">Download</T>
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={sheetName || null}
          onValueChange={(name) => {
            setSheetName(name);
            setStartRow(1);
          }}
        >
          <SelectTrigger
            size="sm"
            className="min-w-36"
            aria-label={gt('xlsx sheet', {
              context: 'workbook file sheet selector',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {file?.sheets.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs">
          <T context="workbook column names row">Column names in row</T>
          <Input
            className="w-20"
            type="number"
            min={1}
            max={10000}
            step={1}
            aria-invalid={!validHeader}
            value={headerRow}
            onChange={(event) => setHeaderRow(event.target.value)}
          />
        </label>
        <Button
          size="sm"
          disabled={!canUpdate || !inspection.data || !validHeader}
          onClick={() =>
            onSource({
              id: crypto.randomUUID(),
              name: `${file?.name}: ${sheetName}`,
              folder: '',
              kind: 'file',
              fileId,
              sheetName,
              headerRow: Number(headerRow),
            })
          }
        >
          <PlusIcon />
          <T context="workbook use file as input">Use as data</T>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        <T context="workbook reference guidance">
          Read-only preview of the uploaded xlsx file. Choose the row containing
          column names; rows below it become data when you select Use as data.
        </T>
      </p>
      {inspection.error && (
        <p role="alert" className="text-destructive">
          {inspection.error.message}
        </p>
      )}
      {inspection.data && (
        <>
          <div className="max-h-[50vh] overflow-auto rounded border">
            <table className="w-full text-xs">
              <tbody>
                {inspection.data.rows.map((row, index) => (
                  <tr
                    key={index}
                    className={
                      validHeader && startRow + index === Number(headerRow)
                        ? 'bg-primary/10 font-medium'
                        : ''
                    }
                  >
                    <th className="border bg-muted px-2 py-1">
                      {startRow + index}
                    </th>
                    {row.map((cell) => (
                      <td
                        key={cell.address}
                        className="min-w-28 max-w-80 border px-2 py-1"
                        title={cell.formula ?? cell.numberFormat ?? ''}
                      >
                        {cell.text}
                        {cell.formula && (
                          <FunctionIcon
                            aria-hidden="true"
                            className="ml-1 inline size-3 text-primary"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={startRow === 1}
              onClick={() => setStartRow(Math.max(1, startRow - 30))}
            >
              <T context="workbook previous rows">Previous rows</T>
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!inspection.data.truncated}
              onClick={() => setStartRow(startRow + 30)}
            >
              <T context="workbook next rows">Next rows</T>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
/** Says why a kind of data is missing from the catalog, so an empty panel isn't a mystery. */
export function CatalogGapsHint({
  gaps,
}: {
  gaps: ReturnType<typeof catalogGaps>;
}) {
  const gt = useGT();
  const context = 'workbook catalog kind, in a sentence';
  const locale = useLocale();
  // Lower case: the names sit mid-sentence ("No data sources or data mappings yet.").
  const names: Record<WorkbookCatalogKind, string> = {
    'data-source': gt('data sources', { context }),
    mapping: gt('data mappings', { context }),
    'stateful-table': gt('stateful tables', { context }),
    'data-asset': gt('data assets', { context }),
    entity: gt('entities', { context }),
    'forecast-snapshots': gt('forecast snapshots', { context }),
  };
  const list = (kinds: readonly WorkbookCatalogKind[]) =>
    new Intl.ListFormat(locale, { type: 'disjunction' }).format(
      kinds.map((kind) => names[kind]),
    );
  if (!gaps.empty.length && !gaps.unreadable.length) return null;
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      {gaps.empty.length > 0 && (
        <p>
          {gt(
            'No {kinds} yet. An admin adds them under Workflows, and they appear here.',
            {
              context: 'workbook catalog empty kinds hint',
              kinds: list(gaps.empty),
            },
          )}
        </p>
      )}
      {gaps.unreadable.length > 0 && (
        <p>
          {gt("You can't read {kinds}. Ask an admin for access to use them.", {
            context: 'workbook catalog unreadable kinds hint',
            kinds: list(gaps.unreadable),
          })}
        </p>
      )}
    </div>
  );
}
